import { useState, useEffect, useRef, useCallback } from "react";
import { Volume2, VolumeX } from "lucide-react";

const AudioController = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioCtxRef = useRef(null);
  const nodesRef = useRef([]);

  const createAmbientSound = useCallback(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;
    const nodes = [];

    // Master gain
    const master = ctx.createGain();
    master.gain.value = 0.12;
    master.connect(ctx.destination);

    // Low drone
    const drone = ctx.createOscillator();
    drone.type = "sine";
    drone.frequency.value = 55;
    const droneGain = ctx.createGain();
    droneGain.gain.value = 0.3;
    drone.connect(droneGain);
    droneGain.connect(master);
    drone.start();
    nodes.push(drone);

    // Mid hum
    const hum = ctx.createOscillator();
    hum.type = "sine";
    hum.frequency.value = 110;
    const humGain = ctx.createGain();
    humGain.gain.value = 0.15;
    hum.connect(humGain);
    humGain.connect(master);
    hum.start();
    nodes.push(hum);

    // High shimmer with LFO
    const shimmer = ctx.createOscillator();
    shimmer.type = "sine";
    shimmer.frequency.value = 880;
    const shimmerGain = ctx.createGain();
    shimmerGain.gain.value = 0.02;
    shimmer.connect(shimmerGain);
    shimmerGain.connect(master);
    shimmer.start();
    nodes.push(shimmer);

    // LFO for shimmer volume
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 0.15;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.015;
    lfo.connect(lfoGain);
    lfoGain.connect(shimmerGain.gain);
    lfo.start();
    nodes.push(lfo);

    // Filtered noise for ambient texture
    const bufferSize = ctx.sampleRate * 2;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.5;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.value = 300;
    noiseFilter.Q.value = 0.5;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.04;
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(master);
    noise.start();
    nodes.push(noise);

    // Periodic beep (sonar-like)
    const beepInterval = setInterval(() => {
      if (audioCtxRef.current && audioCtxRef.current.state === "running") {
        const beep = ctx.createOscillator();
        beep.type = "sine";
        beep.frequency.value = 1200 + Math.random() * 400;
        const beepGain = ctx.createGain();
        beepGain.gain.setValueAtTime(0.03, ctx.currentTime);
        beepGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        beep.connect(beepGain);
        beepGain.connect(master);
        beep.start(ctx.currentTime);
        beep.stop(ctx.currentTime + 0.3);
      }
    }, 4000 + Math.random() * 3000);

    nodesRef.current = { nodes, master, beepInterval };
  }, []);

  const toggleAudio = useCallback(() => {
    if (isPlaying) {
      // Stop
      if (nodesRef.current.beepInterval) clearInterval(nodesRef.current.beepInterval);
      if (nodesRef.current.nodes) {
        nodesRef.current.nodes.forEach((n) => {
          try { n.stop(); } catch (e) { /* already stopped */ }
        });
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
      setIsPlaying(false);
    } else {
      // Start
      createAmbientSound();
      setIsPlaying(true);
    }
  }, [isPlaying, createAmbientSound]);

  useEffect(() => {
    return () => {
      if (nodesRef.current.beepInterval) clearInterval(nodesRef.current.beepInterval);
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch (e) { /* */ }
      }
    };
  }, []);

  return (
    <button
      data-testid="audio-toggle"
      onClick={toggleAudio}
      className="fixed bottom-2 right-3 z-50 flex items-center gap-1.5 bg-cyan-950/50 border border-cyan-500/20 rounded px-2 py-1 cursor-pointer transition-colors duration-300 hover:bg-cyan-900/40 hover:border-cyan-400/30 group"
      style={{ cursor: "pointer" }}
    >
      {isPlaying ? (
        <Volume2 className="w-3.5 h-3.5 text-cyan-400 group-hover:text-cyan-300" />
      ) : (
        <VolumeX className="w-3.5 h-3.5 text-cyan-600 group-hover:text-cyan-400" />
      )}
      <span className="font-mono text-[9px] tracking-wider text-cyan-500 group-hover:text-cyan-300">
        {isPlaying ? "AUDIO ON" : "AUDIO OFF"}
      </span>
      {isPlaying && (
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)] animate-pulse" />
      )}
    </button>
  );
};

export default AudioController;
